import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ChevronDown, CloudUpload, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";
import { api, errMsg } from "@/lib/api";
import { useStorageStatus } from "@/components/layout/OfflineBanner";
import { StorageCard } from "./StorageCard";
import { ServerHealth } from "./ServerHealth";
import { RecoveryKeyCard } from "./RecoveryKeyCard";
import { OneDriveCard } from "./OneDriveCard";
import { ResetLibrary } from "./ResetLibrary";

const ACTIVE = new Set(["queued", "processing"]);
const gb = (b) => `${((b || 0) / 1e9).toFixed(2)} GB`;
const ago = (d) => {
  if (!d) return "never";
  const m = Math.round((Date.now() - new Date(d)) / 60000);
  return m < 1 ? "just now" : m < 60 ? `${m} min ago` : m < 1440 ? `${Math.round(m / 60)} h ago` : new Date(d).toLocaleDateString();
};
const when = (d) => (d ? new Date(d).toLocaleString() : "—");

const Line = ({ label, children, testid }) => (
  <div className="storage-line" data-testid={testid}><span>{label}</span><div>{children}</div></div>
);

const History = ({ title, rows, testid, render }) => (
  <section className="settings-card" data-testid={testid}>
    <h2 className="text-base font-semibold text-neutral-900">{title}</h2>
    {!rows?.length ? <p className="text-sm text-neutral-500 mt-2">Nothing yet.</p> : (
      <ul className="storage-history">{rows.map((r) => <li key={r.id}><span>{when(r.finished_at || r.created_at)}</span><span className={`status-pill ${r.status}`}>{r.status}</span><span>{render(r)}</span></li>)}</ul>
    )}
  </section>
);


export const StorageTab = () => {
  const [fj, setFj] = useState(null);
  const [gd, setGd] = useState(null);
  const health = useStorageStatus();
  const [st, setSt] = useState(null);
  const [adv, setAdv] = useState(() => window.location.hash === "#recovery-key");
  const { hash } = useLocation();
  useEffect(() => { if (hash === "#recovery-key") setAdv(true); }, [hash]);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => Promise.all([api.get("/admin/storage").then((r) => setSt(r.data)),
    api.get("/onedrive/export").then((r) => setFj(r.data)), api.get("/onedrive/status").then((r) => setGd(r.data))]), []);
  const refreshFj = load;
  const syncing = false;
  const exporting = ACTIVE.has(fj?.backup?.status);
  useEffect(() => {
    load();
    if (!syncing && !exporting) return undefined;
    const t = setInterval(() => { load(); refreshFj(); }, 3000);
    return () => clearInterval(t);
  }, [load, refreshFj, syncing, exporting]);
  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); await load(); await refreshFj(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const connected = gd?.connected && gd?.state === "ok";
  const fjLabel = !gd?.connected ? "Not connected" : gd.state !== "ok" || health.drive === "reconnect" ? "Reconnect needed"
    : health.drive === "down" ? "Temporarily unavailable" : `Connected as ${gd.email}`;
  const dot = fjLabel.startsWith("Connected") ? "ok" : fjLabel === "Temporarily unavailable" ? "warn" : "bad";
  const snap = st?.migration?.after || st?.snapshots?.slice(-1)[0];
  const used = snap?.keep_bytes || 0;
  const total = snap?.total_bytes || 0;
  const budget = 5e9;

  return (
    <div className="settings-stack" data-testid="admin-storage-tab">
      <section className="settings-card" data-testid="storage-summary-card">
        <h2 className="text-base font-semibold text-neutral-900">Storage</h2>
        <div className="storage-lines">
          <Line label="OneDrive" testid="storage-summary-onedrive">
            <span className={`storage-dot ${dot}`} /> {fjLabel}
            <small>Last check {ago(health.detail?.last_ok_at || health.detail?.last_ping)}</small>
          </Line>
          <Line label="Photos" testid="storage-summary-photos">{(st?.in_drive ?? 0).toLocaleString()} photos & videos stored in OneDrive</Line>
          <Line label="Site storage" testid="storage-summary-site">
            <Progress value={Math.min(100, (used / budget) * 100)} className="h-2 mt-1 mb-1" />
            {gb(used)} of 5 GB used (previews)
            {total > budget && <small className="!text-amber-700" data-testid="storage-over-limit-note">Site storage still holds {gb(total)} (moved originals included): the clean-up email to Emergent support is still needed.</small>}
          </Line>
        </div>
        <div className="flex flex-wrap gap-2 mt-5">
          <button className="btn-outline inline-flex items-center gap-2" disabled={!connected || exporting || !!busy} onClick={() => run("export", () => api.post("/onedrive/export"))} data-testid="drive-export-button">
            {busy === "export" || exporting ? <Loader2 size={16} className="animate-spin" /> : <CloudUpload size={16} />} Export albums to OneDrive
          </button>
          <a className="btn-outline inline-flex items-center gap-2" href="https://onedrive.live.com" target="_blank" rel="noreferrer" data-testid="open-onedrive-link"><ExternalLink size={16} /> Open OneDrive</a>
        </div>
      </section>
      <OneDriveCard />
      <button className="storage-adv-toggle" onClick={() => setAdv(!adv)} aria-expanded={adv} data-testid="storage-advanced-toggle">
        Advanced <ChevronDown size={16} className={`transition-transform ${adv ? "rotate-180" : ""}`} />
      </button>
      {adv && (
        <div className="settings-stack" data-testid="storage-advanced">
          <ServerHealth />
          <RecoveryKeyCard />
          <ResetLibrary />
          <StorageCard />
          <History title="Export history" testid="export-history" rows={fj?.backup_history}
            render={(r) => `${r.counts?.uploaded || 0} copied, ${r.counts?.skipped || 0} already there, ${r.counts?.failed || 0} failed`} />
        </div>
      )}
    </div>
  );
};
